/**
 * Tab "Gestiones" del detalle de OT.
 *
 * Bitácora administrativa: el admin/master deja constancia de comentarios,
 * llamadas, comunicaciones con cliente/aseguradora, emails enviados,
 * recordatorios, etc. El técnico solo ve las marcadas como "visible_tecnico".
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ClipboardList, Plus, Trash2, MessageCircle, Phone, Building2,
  Mail as MailIcon, Bell, FileText, Eye, EyeOff, ArrowRightLeft,
  Truck, XCircle, ShieldCheck, Send, CornerDownRight, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const TIPOS = [
  { value: 'comentario', label: 'Comentario interno', icon: MessageCircle, color: 'bg-slate-100 text-slate-700' },
  { value: 'llamada_cliente', label: 'Llamada al cliente', icon: Phone, color: 'bg-blue-100 text-blue-700' },
  { value: 'whatsapp', label: 'WhatsApp al cliente', icon: MessageSquare, color: 'bg-green-100 text-green-700' },
  { value: 'comunicacion_aseguradora', label: 'Comunicación con aseguradora', icon: Building2, color: 'bg-purple-100 text-purple-700' },
  { value: 'email', label: 'Email enviado', icon: MailIcon, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'recordatorio', label: 'Recordatorio', icon: Bell, color: 'bg-amber-100 text-amber-700' },
  { value: 'otro', label: 'Otro', icon: FileText, color: 'bg-gray-100 text-gray-700' },
];

// Accesos rápidos para identificar tipos de contacto frecuentes con el cliente
const QUICK_ACTIONS = [
  {
    value: 'llamada_cliente',
    label: 'Llamada',
    icon: Phone,
    btnClass: 'border-blue-300 text-blue-700 hover:bg-blue-50',
    placeholder: 'Ej: Llamé al cliente para confirmar disponibilidad. Acordamos…',
  },
  {
    value: 'whatsapp',
    label: 'WhatsApp',
    icon: MessageSquare,
    btnClass: 'border-green-400 text-green-700 hover:bg-green-50',
    placeholder: 'Ej: Le escribí por WhatsApp. Cliente confirma recepción / autoriza / pide…',
  },
  {
    value: 'email',
    label: 'Correo',
    icon: MailIcon,
    btnClass: 'border-emerald-400 text-emerald-700 hover:bg-emerald-50',
    placeholder: 'Ej: Email enviado con presupuesto/seguimiento al cliente. Asunto: …',
  },
];

// Tipos automáticos (no aparecen en el dialog de creación)
const TIPOS_AUTO = {
  cambio_estado: { value: 'cambio_estado', label: 'Cambio de estado', icon: ArrowRightLeft, color: 'bg-indigo-100 text-indigo-700' },
  envio_gls_creado: { value: 'envio_gls_creado', label: 'Envío GLS creado', icon: Truck, color: 'bg-cyan-100 text-cyan-700' },
  envio_gls_anulado: { value: 'envio_gls_anulado', label: 'Envío GLS anulado', icon: XCircle, color: 'bg-red-100 text-red-700' },
  cpi_nist: { value: 'cpi_nist', label: 'CPI/NIST registrado', icon: ShieldCheck, color: 'bg-teal-100 text-teal-700' },
};

function tipoMeta(tipo) {
  if (TIPOS_AUTO[tipo]) return TIPOS_AUTO[tipo];
  return TIPOS.find(t => t.value === tipo) || TIPOS[TIPOS.length - 1];
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function OrdenGestionesTab({ orden, onUpdate }) {
  const { user } = useAuth();
  const isMaster = (user?.role || '').toLowerCase() === 'master';
  const isAdmin = ['admin', 'master'].includes((user?.role || '').toLowerCase());

  const [gestiones, setGestiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tipo: 'comentario', texto: '', visible_tecnico: false });
  const [placeholder, setPlaceholder] = useState('');
  const [respuestaTexto, setRespuestaTexto] = useState({}); // { [gestionId]: 'texto…' }
  const [enviandoRespuesta, setEnviandoRespuesta] = useState({});

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ordenes/${orden.id}/gestiones`);
      setGestiones(data.items || []);
    } catch (e) {
      toast.error('No se pudieron cargar las gestiones');
    } finally {
      setLoading(false);
    }
  }, [orden.id]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleAbrirDialog = (preset = null) => {
    if (preset) {
      setForm({ tipo: preset.value, texto: '', visible_tecnico: false });
      setPlaceholder(preset.placeholder || '');
    } else {
      setForm({ tipo: 'comentario', texto: '', visible_tecnico: false });
      setPlaceholder('Ej: He llamado al cliente para confirmar que recibió el presupuesto. Está conforme y autoriza la reparación.');
    }
    setShowDialog(true);
  };

  const handleEnviarRespuesta = async (gestionId) => {
    const texto = (respuestaTexto[gestionId] || '').trim();
    if (!texto) return;
    setEnviandoRespuesta(prev => ({ ...prev, [gestionId]: true }));
    try {
      await api.post(`/ordenes/${orden.id}/gestiones/${gestionId}/respuestas`, { texto });
      setRespuestaTexto(prev => ({ ...prev, [gestionId]: '' }));
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo añadir la respuesta');
    } finally {
      setEnviandoRespuesta(prev => ({ ...prev, [gestionId]: false }));
    }
  };

  const handleBorrarRespuesta = async (gestionId, respuestaId) => {
    if (!window.confirm('¿Borrar este mensaje del hilo?')) return;
    try {
      await api.delete(`/ordenes/${orden.id}/gestiones/${gestionId}/respuestas/${respuestaId}`);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const handleGuardar = async () => {
    if (!form.texto.trim()) {
      toast.error('Escribe el texto de la gestión');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/ordenes/${orden.id}/gestiones`, form);
      toast.success('Gestión registrada');
      setShowDialog(false);
      cargar();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo registrar');
    } finally {
      setSaving(false);
    }
  };

  const handleBorrar = async (g) => {
    if (!window.confirm('¿Borrar esta gestión? Esta acción no se puede deshacer.')) return;
    try {
      await api.delete(`/ordenes/${orden.id}/gestiones/${g.id}`);
      toast.success('Gestión eliminada');
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const puedeBorrar = (g) => !g.auto && (isMaster || g.usuario_email === user?.email);

  return (
    <div className="space-y-4" data-testid="orden-gestiones-tab">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              Gestiones administrativas ({gestiones.length})
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Registro de comentarios, llamadas, comunicaciones y recordatorios sobre esta OT
            </p>
          </div>
          {isAdmin && (
            <Button onClick={() => handleAbrirDialog()} size="sm" data-testid="gestiones-nueva">
              <Plus className="w-4 h-4 mr-1" />
              Nueva gestión
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {/* Quick actions: registrar tipo de contacto con el cliente con un solo clic */}
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-lg bg-muted/40 border border-dashed">
              <span className="text-[11px] uppercase font-semibold text-muted-foreground mr-1">
                Registrar contacto rápido:
              </span>
              {QUICK_ACTIONS.map(qa => {
                const Icon = qa.icon;
                return (
                  <Button
                    key={qa.value}
                    variant="outline"
                    size="sm"
                    className={`h-8 ${qa.btnClass}`}
                    onClick={() => handleAbrirDialog(qa)}
                    data-testid={`gestion-quick-${qa.value}`}
                  >
                    <Icon className="w-3.5 h-3.5 mr-1.5" />
                    {qa.label}
                  </Button>
                );
              })}
            </div>
          )}
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-6">Cargando…</p>
          ) : gestiones.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">
              <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
              No hay gestiones registradas todavía
              {isAdmin && (
                <p className="text-xs mt-1">
                  Pulsa &ldquo;Nueva gestión&rdquo; para dejar constancia de cualquier comunicación o acción.
                </p>
              )}
            </div>
          ) : (
            <ol className="relative border-l-2 border-muted ml-3 space-y-4">
              {gestiones.map(g => {
                const meta = tipoMeta(g.tipo);
                const Icon = meta.icon;
                return (
                  <li key={g.id} className="ml-4" data-testid={`gestion-${g.id}`}>
                    <span className={`absolute -left-[14px] flex items-center justify-center w-7 h-7 rounded-full ring-4 ring-background ${meta.color}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <div className="bg-muted/30 rounded-lg p-3 border">
                      <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
                          {g.auto ? (
                            <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-300">
                              Automático
                            </Badge>
                          ) : g.visible_tecnico ? (
                            <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-300">
                              <Eye className="w-2.5 h-2.5 mr-0.5" />
                              Visible técnico
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">
                              <EyeOff className="w-2.5 h-2.5 mr-0.5" />
                              Solo admin
                            </Badge>
                          )}
                        </div>
                        <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                          {formatDate(g.fecha)}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{g.texto}</p>

                      {/* Hilo de respuestas (solo gestiones manuales) */}
                      {!g.auto && (
                        <div className="mt-3 space-y-2" data-testid={`hilo-respuestas-${g.id}`}>
                          {(g.respuestas || []).map(r => (
                            <div
                              key={r.id}
                              className="flex items-start gap-2 pl-3 border-l-2 border-primary/30 bg-background/50 rounded-r p-2"
                              data-testid={`respuesta-${r.id}`}
                            >
                              <CornerDownRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm whitespace-pre-wrap">{r.texto}</p>
                                <div className="flex items-center justify-between mt-1 flex-wrap gap-1">
                                  <p className="text-[10px] text-muted-foreground">
                                    {r.usuario_nombre || r.usuario_email} · {formatDate(r.fecha)}
                                  </p>
                                  {(isMaster || r.usuario_email === user?.email) && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 w-5 p-0 text-red-600 hover:text-red-700"
                                      onClick={() => handleBorrarRespuesta(g.id, r.id)}
                                      data-testid={`respuesta-borrar-${r.id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Input para añadir mensaje al hilo */}
                          {isAdmin && (
                            <div className="flex items-end gap-2 pl-3">
                              <Textarea
                                rows={2}
                                placeholder="Añadir mensaje de tramitación al hilo…"
                                value={respuestaTexto[g.id] || ''}
                                onChange={(e) => setRespuestaTexto(prev => ({ ...prev, [g.id]: e.target.value }))}
                                className="text-sm flex-1"
                                data-testid={`respuesta-input-${g.id}`}
                              />
                              <Button
                                size="sm"
                                onClick={() => handleEnviarRespuesta(g.id)}
                                disabled={!respuestaTexto[g.id]?.trim() || enviandoRespuesta[g.id]}
                                data-testid={`respuesta-enviar-${g.id}`}
                              >
                                <Send className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-muted/50 flex-wrap gap-2">
                        <p className="text-[11px] text-muted-foreground">
                          {g.usuario_nombre || g.usuario_email}
                        </p>
                        {puedeBorrar(g) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-red-600 hover:text-red-700"
                            onClick={() => handleBorrar(g)}
                            data-testid={`gestion-borrar-${g.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nueva Gestión */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="gestion-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Nueva gestión administrativa
            </DialogTitle>
            <DialogDescription>
              Deja constancia de una acción realizada sobre esta OT.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs">Tipo de gestión</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm(f => ({ ...f, tipo: v }))}>
                <SelectTrigger data-testid="gestion-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map(t => {
                    const Icon = t.icon;
                    return (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5" />
                          {t.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Detalle de la gestión *</Label>
              <Textarea
                rows={6}
                value={form.texto}
                onChange={(e) => setForm(f => ({ ...f, texto: e.target.value }))}
                placeholder={placeholder || 'Ej: He llamado al cliente para confirmar que recibió el presupuesto. Está conforme y autoriza la reparación.'}
                data-testid="gestion-texto"
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
              <Checkbox
                id="visible-tecnico"
                checked={form.visible_tecnico}
                onCheckedChange={(v) => setForm(f => ({ ...f, visible_tecnico: !!v }))}
                data-testid="gestion-visible-tecnico"
              />
              <Label htmlFor="visible-tecnico" className="text-xs cursor-pointer flex-1">
                <span className="font-medium">Visible para el técnico</span>
                <span className="block text-muted-foreground">
                  Si está marcada, el técnico podrá ver esta gestión en su vista de OT.
                </span>
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleGuardar} disabled={saving} data-testid="gestion-guardar">
              {saving ? 'Guardando…' : 'Registrar gestión'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
